import {
  Inject,
  Injectable,
  Optional,
  Renderer2,
  SkipSelf,
  InjectionToken,
  DOCUMENT,
} from "@angular/core";

import { WINDOW } from "shared/helper-services/window-token.module";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";

@Injectable()
export class BfxMouseModifierKeyService {
  private _ctrlKey: boolean = false;
  private _shiftKey: boolean = false;
  private _metaKey: boolean = false;
  private _altKey: boolean = false;

  private _mouseDown: boolean = false;

  private _mouseLeft: boolean = false;
  private _mouseMiddle: boolean = false;
  private _mouseRight: boolean = false;

  private _mouseDownTarget: string = null;
  private _mouseUpTarget: string = null;

  /**
   * this is best used as a log of last modifier state mousedown/up
   * for cases that involve third-party mouseevent handling
   * without enough callback info (such as ag-grid range selection)
   *
   * @param document
   * @param window
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    @Inject(WINDOW) private window: any,
  ) {
    let ctrlMouseDownRef = this.onCtrlMouseDown.bind(this);
    document.addEventListener("mousedown", ctrlMouseDownRef, { passive: true });

    let ctrlMouseUpRef = this.onCtrlMouseUp.bind(this);
    document.addEventListener("mouseup", ctrlMouseUpRef, { passive: true });
  }

  public registerDocument(document: Document): void {
    logger.out.info("Registering new document ... ");

    let ctrlMouseDownRef = this.onCtrlMouseDown.bind(this);
    document.addEventListener("mousedown", ctrlMouseDownRef, { passive: true });

    let ctrlMouseUpRef = this.onCtrlMouseUp.bind(this);
    document.addEventListener("mouseup", ctrlMouseUpRef, { passive: true });
  }

  public onCtrlMouseDown($event) {
    this._altKey = $event.altKey;
    this._ctrlKey = $event.ctrlKey;
    this._metaKey = $event.metaKey;
    this._shiftKey = $event.shiftKey;

    this._mouseLeft = $event.button === 0;
    this._mouseMiddle = $event.button === 1;
    this._mouseRight = $event.button === 2;

    this._mouseDown = true;

    this._mouseDownTarget = $event.target.className;
  }

  protected onCtrlMouseUp($event) {
    this._altKey = $event.altKey;
    this._ctrlKey = $event.ctrlKey;
    this._metaKey = $event.metaKey;
    this._shiftKey = $event.shiftKey;

    this._mouseLeft = $event.button === 0;
    this._mouseMiddle = $event.button === 1;
    this._mouseRight = $event.button === 2;

    this._mouseDown = false;

    this._mouseUpTarget = $event.target.className;
  }

  public altKey(): boolean {
    return this._altKey;
  }

  public ctrlKey(): boolean {
    return this._ctrlKey;
  }

  public metaKey(): boolean {
    return this._metaKey;
  }

  public shiftKey(): boolean {
    return this._shiftKey;
  }

  /**
   * Warning: most processing will occur after mouseup!
   */
  public isMouseDown(): boolean {
    return this._mouseDown;
  }

  public isMouseLeft(): boolean {
    return this._mouseLeft;
  }

  public isMouseMiddle(): boolean {
    return this._mouseMiddle;
  }

  public isMouseRight(): boolean {
    return this._mouseMiddle;
  }

  public isTargetClassName(target: string): boolean {
    // examples
    // ag-icon ag-icon-checkbox-unchecked
    // ag-icon ag-icon-checkbox-checked

    return this._mouseDownTarget === this._mouseUpTarget && this._mouseUpTarget === target;
  }

  public isTargetCheckbox(): boolean {
    let uncheck = `ag-icon ag-icon-checkbox-unchecked`;
    let check = `ag-icon ag-icon-checkbox-checked`;
    let indeterminate = `ag-icon ag-icon-checkbox-indeterminate`;
    return (
      (this._mouseDownTarget === uncheck ||
        this._mouseDownTarget === check ||
        this._mouseDownTarget === indeterminate) &&
      (this._mouseUpTarget === uncheck ||
        this._mouseUpTarget === check ||
        this._mouseUpTarget === indeterminate)
    );
  }

  public isTargetDownCheckbox(): boolean {
    let uncheck = `ag-icon ag-icon-checkbox-unchecked`;
    let check = `ag-icon ag-icon-checkbox-checked`;
    let indeterminate = `ag-icon ag-icon-checkbox-indeterminate`;
    return (
      this._mouseDownTarget === uncheck ||
      this._mouseDownTarget === check ||
      this._mouseDownTarget === indeterminate
    );
  }

  public getMouseTargetDown(): string {
    return this._mouseDownTarget;
  }

  public getMouseTargetUp(): string {
    return this._mouseUpTarget;
  }
}
